import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Container, List, Button, Icon } from '@pella/sash-framework';
import { classNames } from '@pella/sash-framework/dist/utilities';
import { formatPhoneNumber } from '../../utilities/functions';
import { useBranchContext } from '../../../context/BranchContext';

import CTA from '../cta';

import './index.css';

const LocalNavTop = ({ navTitle, linksArray, ctaButton, children, className, isAnchors = true, branchData, overRidePhone, currentUrl, ...other }) => {
	const { updateCurrentBranch } = useBranchContext();
	const defaultActiveLink = isAnchors ? 'Jump to..' : !isAnchors && linksArray?.find((link) => link?.link?.href === currentUrl) ? linksArray?.find((link) => link.link.href === currentUrl)?.link.title : '<span class="material-symbols-sharp leading-none mr-1.5">house</span> Home';
	const [showLinks, setShowLinks] = useState(false);
	const [activeLink, setActiveLink] = useState(defaultActiveLink);
	const renderPhoneNumber = overRidePhone || branchData?.phone_number;

	// eslint-disable-next-line react-hooks/exhaustive-deps -- run on mount only
	useEffect(() => updateCurrentBranch(branchData), []);

	useEffect(() => {
		if (!branchData) return;

		window.dataLayer = window.dataLayer || [];
		window.dataLayer.push({ event: 'branchFlowID', branchID: branchData?.branch_retail?.[0] });
	}, [branchData]);

	if (!linksArray?.[0]) return null;

	const onTriggerClick = () => setShowLinks(!showLinks);

	const onLinkClick = (title) => {
		if (title) setActiveLink(title);
		setShowLinks(false);
	};

	return (
		<div
			className={`localNavTop sticky top-[59px] z-[19] w-full bg-black py-2 duration-300 sm:top-[63px] md:py-0 xl:top-[98px] ${className || ''}`}
			{...other}
		>
			<Container className='md:!pr-0'>
				<div className={`md:flex md:flex-row md:flex-wrap`}>
					{(navTitle || branchData?.formal_name) && (
						<div className='localNavTop-navTitle flex flex-row pb-3 text-sm font-semibold text-white md:mr-4 md:flex-[0_0_200px] md:flex-col md:justify-center md:pt-3 md:leading-tight xl:mr-12 xl:flex-[0_0_220px] xl:text-base [&_span]:block [&_span]:uppercase'>
							{navTitle && (
								<div
									className='flex-1'
									dangerouslySetInnerHTML={{ __html: navTitle }}
								/>
							)}

							{branchData?.branch_url && (
								<>
									{branchData.branch_url !== currentUrl ? (
										<CTA
											data={{ link: { href: branchData.branch_url } }}
											className='block flex-1 text-white no-underline hover:text-white/90 max-md:pointer-events-none'
											aria-label='Local Nav Branch Link'
										>
											<span>{branchData.formal_name}</span>
										</CTA>
									) : (
										<span className='flex-1'>{branchData.formal_name}</span>
									)}

									{renderPhoneNumber && (
										<span className='flex-1 text-sm max-md:self-end max-md:text-right md:mt-0.5 xl:mt-0 xl:text-base'>
											<a
												href={`tel:${renderPhoneNumber}`}
												title={`Call ${branchData.formal_name}`}
												className='text-white no-underline'
											>
												{formatPhoneNumber(renderPhoneNumber)}
											</a>
										</span>
									)}
								</>
							)}
						</div>
					)}

					<Button
						color='primary-alt'
						className='localNavTop-button !flex !w-full flex-row items-center justify-between border-2 border-white !p-2 !text-white max-md:mx-auto max-md:!pl-3 md:!hidden'
						size='sm'
						onClick={onTriggerClick}
					>
						<span
							className='localNavTop-buttonText flex flex-1 items-center text-left'
							dangerouslySetInnerHTML={{ __html: activeLink }}
						/>

						<Icon
							name='arrow_drop_down'
							className='ml-2 flex-none'
							size={30}
						/>
					</Button>

					<List
						bullets='none'
						className={`localNavTop-links max-md:space-y-4 max-md:border-2 max-md:border-t-0 max-md:border-white max-md:px-4 max-md:py-4 md:flex-1 ${showLinks ? 'max-md:block' : 'max-md:hidden'}`}
						layout='inline'
					>
						{branchData?.branch_url && (
							<li className='md:hidden'>
								<CTA
									data={{ link: { href: branchData.branch_url, title: 'Home' }, material_icon: 'house' }}
									className='!flex h-full items-center font-semibold text-white !no-underline transition-colors hover:opacity-100 max-md:uppercase md:border-y-4 md:border-b-transparent md:border-t-transparent md:py-3 md:normal-case md:hover:border-b-primary xl:text-base [&_span]:mr-1.5'
									onClick={() => onLinkClick()}
								/>
							</li>
						)}

						{linksArray.map((link, index) => {
							const ctaClasses = classNames({
								'!flex h-full items-center font-semibold text-white transition-colors hover:opacity-100 max-md:uppercase md:normal-case md:border-y-4 md:border-t-transparent md:py-3 md:hover:border-b-primary xl:text-base': true,
								'md:border-b-primary md:!no-underline localNavTop-activeLink': isAnchors && activeLink === link.link.title,
								'!no-underline md:border-b-transparent hover:!opacity-100': (isAnchors && activeLink !== link.link.title) || !isAnchors,
								'md:!border-b-primary': !isAnchors && currentUrl === link.link.href,
							});

							return (
								<li
									key={index}
									className={`max-md:max-w-full max-md:!flex-12/12 md:text-sm xl:text-base ${index > 0 ? 'md:ml-4 xl:ml-8' : ''}`}
								>
									<CTA
										data={link}
										className={ctaClasses}
										onClick={() => onLinkClick(isAnchors ? link.link.title : '')}
									/>
								</li>
							);
						})}
					</List>

					{(ctaButton || children) && (
						<div
							data-testid='localnav-children'
							className='flex items-center py-4 md:flex-none md:py-1.5'
						>
							{ctaButton && <CTA data={ctaButton} />}
							{children}
						</div>
					)}
				</div>
			</Container>
		</div>
	);
};

LocalNavTop.propTypes = {
	navTitle: PropTypes.any,
	linksArray: PropTypes.array.isRequired,
	ctaButton: PropTypes.object,
	children: PropTypes.node,
	className: PropTypes.string,
	isAnchors: PropTypes.bool,
	branchData: PropTypes.object,
	currentUrl: PropTypes.string,
	overRidePhone: PropTypes.string,
};

export default LocalNavTop;
